import type { Dispatch, SetStateAction } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EMAIL_RECIPIENT_HARD_CAP } from "@/lib/composerCore";
import type { EditorState } from "../connectorManagerHelpers";

type Props = {
  editor: EditorState;
  setEditor: Dispatch<SetStateAction<EditorState>>;
  addRecipientFromDraft: () => void;
  removeRecipient: (email: string) => void;
};

export const EmailFields = ({ editor, setEditor, addRecipientFromDraft, removeRecipient }: Props) => (
  <>
    <div className="grid gap-2 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="connector-from-name">From name (optional)</Label>
        <Input
          id="connector-from-name"
          value={editor.fromName}
          onChange={(e) => setEditor((s) => ({ ...s, fromName: e.target.value }))}
          placeholder="e.g. Excerptus Weekly"
        />
        <p className="text-xs text-muted-foreground">Sender address uses our verified domain.</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="connector-reply-to">Reply-to (optional)</Label>
        <Input
          id="connector-reply-to"
          type="email"
          value={editor.replyTo}
          onChange={(e) => setEditor((s) => ({ ...s, replyTo: e.target.value }))}
          placeholder="you@example.com"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="connector-recipients">
        Recipients ({editor.recipients.length}/{EMAIL_RECIPIENT_HARD_CAP})
      </Label>
      <div className="flex gap-2">
        <Input
          id="connector-recipients"
          type="email"
          value={editor.recipientDraft}
          onChange={(e) => setEditor((s) => ({ ...s, recipientDraft: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addRecipientFromDraft();
            }
          }}
          placeholder="recipient@example.com"
        />
        <Button type="button" variant="outline" onClick={addRecipientFromDraft}>
          Add
        </Button>
      </div>
      {editor.recipients.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 rounded-md border border-border bg-muted/40 p-2">
          {editor.recipients.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-xs"
            >
              {email}
              <button
                type="button"
                onClick={() => removeRecipient(email)}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Remove ${email}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Add 1–{EMAIL_RECIPIENT_HARD_CAP} email addresses. Press Enter or comma to add.
        </p>
      )}
    </div>

    <div className="space-y-2">
      <Label htmlFor="connector-subject">Subject template</Label>
      <Input
        id="connector-subject"
        value={editor.subjectTemplate}
        onChange={(e) => setEditor((s) => ({ ...s, subjectTemplate: e.target.value }))}
        placeholder="{{title}}"
      />
      <p className="text-xs text-muted-foreground">
        Use <code>{"{{title}}"}</code> to insert the publication title. Defaults to the title.
      </p>
    </div>
  </>
);
